import React, { useState, useEffect, useRef } from "react";
import { getImageUrl } from "../../utils/imageHelper";

/**
 * ImagesGrid Component
 * Renders a horizontal carousel of images with lightbox and loading states
 */
const ImagesGrid = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || images.length === 0) return null;

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "10px",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(83, 45, 108, 0.3) transparent",
          paddingTop: "4px",
          paddingBottom: "8px",
          marginTop: "10px",
          marginBottom: "10px",
        }}
        className="images-grid-carousel"
      >
        {images.map((image, index) => {
          const resolvedUrl = getImageUrl(image) || image;
          if (!resolvedUrl || typeof resolvedUrl !== "string") return null;
          return (
            <ImageItem
              key={`img-${index}-${String(resolvedUrl).slice(0, 80)}`}
              image={resolvedUrl}
              index={index}
              onClick={() => handleImageClick(resolvedUrl)}
            />
          );
        })}
      </div>

      {/* Lightbox - use resolved URL */}
      {selectedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
          onClick={closeLightbox}
        >
          <img
            src={selectedImage}
            alt="Full size"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
          />
          <button
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              fontSize: "24px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={closeLightbox}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

const ImageItem = ({ image, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  // Reset state when image URL changes (e.g. reused component with new src)
  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [image]);

  // Handle cached images: onLoad often doesn't fire when served from cache.
  // Check img.complete && naturalWidth > 0 after mount/update.
  useEffect(() => {
    const el = imgRef.current;
    if (!el || !image) return;
    if (el.complete && el.naturalWidth > 0) {
      setLoaded(true);
      setError(false);
    }
  }, [image, loaded, error]);

  return (
    <div
      style={{
        position: "relative",
        minWidth: "180px",
        maxWidth: "180px",
        height: "180px",
        overflow: "hidden",
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: "#f0f0f0",
        scrollSnapAlign: "start",
        flexShrink: 0,
      }}
      onClick={onClick}
    >
      {!loaded && !error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f0f0f0",
          }}
        >
          <div className="image-loading-spinner"></div>
        </div>
      )}
      <img
        ref={imgRef}
        src={image}
        alt={`Image ${index + 1}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: loaded ? "block" : "none",
        }}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setError(true);
          setLoaded(false);
        }}
      />
      {error && (
        <img
          src="/placeholderImage.png"
          alt="Placeholder"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
};

export default ImagesGrid;
